/**
 * useTasksListViewPreference — Persiste a preferência de visualização
 * (modo lista x agrupada) e o campo de agrupamento da página de Tarefas
 * em `localStorage`, sobrevivendo a navegações e recarregamentos.
 *
 * Chaves utilizadas:
 *   - `tasks:list:view-mode`  → 'list' | 'grouped'
 *   - `tasks:list:group-by`   → TaskGroupBy
 *
 * Padrão: lê uma única vez no init (`useState(initializer)`) e grava no
 * `useEffect` ao mudar. Try/catch protege contra SSR/quota.
 *
 * @version 1.0.0
 * RELEASE: 2026-06-24 v1.0.0 - Criação inicial: persistência de view/groupBy
 */
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import type { TaskGroupBy } from '@/components/tasks/list/TaskGroupedView';

export type TaskListViewMode = 'list' | 'grouped';
export type TaskGroupsDefault = 'collapsed' | 'expanded';

const STORAGE_KEY_VIEW_MODE = 'tasks:list:view-mode';
const STORAGE_KEY_GROUP_BY = 'tasks:list:group-by';
const STORAGE_KEY_GROUPS_DEFAULT = 'tasks:list:groups-default-expanded';

const VALID_VIEW_MODES: TaskListViewMode[] = ['list', 'grouped'];
const VALID_GROUP_BY: TaskGroupBy[] = ['phase_id', 'status', 'priority', 'assigned_to'];
const VALID_GROUPS_DEFAULT: TaskGroupsDefault[] = ['collapsed', 'expanded'];

/** Lê valor do localStorage com fallback seguro. */
function readStorage<T extends string>(key: string, valid: T[], fallback: T): T {
  try {
    if (typeof window === 'undefined') return fallback;
    const raw = window.localStorage.getItem(key);
    if (raw && (valid as string[]).includes(raw)) return raw as T;
  } catch {
    /* ignora SSR/quota/privacy mode */
  }
  return fallback;
}

/** Grava valor no localStorage de forma silenciosa. */
function writeStorage(key: string, value: string) {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, value);
  } catch {
    /* ignora SSR/quota */
  }
}

/**
 * Hook que devolve estado + setter persistido em localStorage para o
 * modo de visualização e o campo de agrupamento da listagem de tarefas.
 */
export function useTasksListViewPreference(
  defaults: {
    viewMode?: TaskListViewMode;
    groupBy?: TaskGroupBy;
    groupsDefault?: TaskGroupsDefault;
  } = {}
) {
  const defaultViewMode: TaskListViewMode = defaults.viewMode ?? 'list';
  const defaultGroupBy: TaskGroupBy = defaults.groupBy ?? 'phase_id';
  const defaultGroupsDefault: TaskGroupsDefault = defaults.groupsDefault ?? 'collapsed';

  // Inicializa lendo do localStorage uma única vez (lazy initializer)
  const [viewMode, setViewMode] = useState<TaskListViewMode>(() =>
    readStorage(STORAGE_KEY_VIEW_MODE, VALID_VIEW_MODES, defaultViewMode)
  );
  const [groupBy, setGroupBy] = useState<TaskGroupBy>(() =>
    readStorage(STORAGE_KEY_GROUP_BY, VALID_GROUP_BY, defaultGroupBy)
  );
  const [groupsDefault, setGroupsDefault] = useState<TaskGroupsDefault>(() =>
    readStorage(STORAGE_KEY_GROUPS_DEFAULT, VALID_GROUPS_DEFAULT, defaultGroupsDefault)
  );

  // Persiste a cada alteração
  useEffect(() => {
    writeStorage(STORAGE_KEY_VIEW_MODE, viewMode);
  }, [viewMode]);

  useEffect(() => {
    writeStorage(STORAGE_KEY_GROUP_BY, groupBy);
  }, [groupBy]);

  useEffect(() => {
    writeStorage(STORAGE_KEY_GROUPS_DEFAULT, groupsDefault);
  }, [groupsDefault]);

  return {
    viewMode,
    setViewMode,
    groupBy,
    setGroupBy,
    groupsDefault,
    setGroupsDefault,
  };
}

/* =========================================================================
 * useTaskGroupExpandedState — Persiste, por escopo, se uma categoria/grupo
 * da listagem agrupada está expandido. Usado por TaskGroupedView para
 * restaurar a escolha do usuário ao voltar para a página.
 *
 * Chave: `tasks:list:groups-expanded:<scope>` (boolean serializado).
 * ========================================================================= */
const STORAGE_KEY_GROUP_EXPANDED_PREFIX = 'tasks:list:groups-expanded:';

export function useTaskGroupExpandedState(
  scope: string,
  fallback: boolean
): [boolean, Dispatch<SetStateAction<boolean>>] {
  const storageKey = STORAGE_KEY_GROUP_EXPANDED_PREFIX + scope;

  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      if (typeof window === 'undefined') return fallback;
      const raw = window.localStorage.getItem(storageKey);
      if (raw === 'true') return true;
      if (raw === 'false') return false;
    } catch {
      /* ignora SSR/quota */
    }
    return fallback;
  });

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(storageKey, expanded ? 'true' : 'false');
    } catch {
      /* ignora SSR/quota */
    }
  }, [storageKey, expanded]);

  return [expanded, setExpanded];
}

